import React, { useMemo } from 'react';
import { Grid } from '@mui/material';
import { Exception } from '@/redux/slices/slice_types';
import { LabelValue } from '@/components/_common';
import ExceptionEnumMap from '@/components/Exceptions/ExceptionEnumMap';
import ExceptionReferenceId from '@/components/Exceptions/References/ExceptionReferenceId';
import PrimaryBasedReferences from '@/components/Exceptions/References/PrimaryBasedReferences';
import { LabelValueDefaultProps } from '@/components/Exceptions/types';

interface Props {
  exception: Exception;
  labelValueProps: LabelValueDefaultProps;
}

const ExceptionReferences: React.FC<Props> = ({ exception, labelValueProps }) => {
  const { references } = exception;
  const primaryReference = useMemo(() => references.find((ref) => ref.is_primary), [references]);
  const referencesByType = useMemo(() => {
    const refs = references || [];
    return refs
      .filter((ref) => !ref.is_primary) // filter out the primary reference, it's rendered separately
      .reduce(
        (acc, ref) => {
          acc[ref.type] = acc[ref.type] || [];
          acc[ref.type].push(ref.id);
          return acc;
        },
        {} as Record<string, Exception['reference_id'][]>
      );
  }, [references]);

  if (!references.length) {
    return <LabelValue label="No references" {...labelValueProps} />;
  }

  return (
    <Grid container alignItems="flex-start" gap={4}>
      {primaryReference && (
        <LabelValue
          size={{ xs: 12 }}
          label={
            <>
              Primary Reference: <ExceptionEnumMap enumRef="reference" value={primaryReference.type} />
            </>
          }
        >
          <ExceptionReferenceId id={primaryReference.id} type={primaryReference.type} />
        </LabelValue>
      )}
      {Object.entries(referencesByType).map(([type, refs], key) => (
        <LabelValue
          key={key}
          label={
            <>
              <ExceptionEnumMap enumRef="reference" value={type} />
              {refs.length > 1 ? 's' : ''}
            </>
          }
          {...labelValueProps}
        >
          {refs.map((ref, index) => (
            <React.Fragment key={index}>
              <ExceptionReferenceId id={ref} type={type} />
              {index < refs.length - 1 ? ', ' : ''}
            </React.Fragment>
          ))}
        </LabelValue>
      ))}

      {primaryReference && <PrimaryBasedReferences primaryReference={primaryReference} labelValueProps={labelValueProps} />}
    </Grid>
  );
};

export default ExceptionReferences;
